import { Component, inject, OnInit, Output } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { Title, Meta } from "@angular/platform-browser";
import { SessionHelper } from "@src/app/helpers/sessionHelper";
import { LoginService } from "./service/login.service";
import { trigger, transition, style, animate } from "@angular/animations";
import { PersonOrganization } from "@src/app/models/personorganization";
import { DatastoreService } from "@src/app/helpers/datastore/datastore.service";
import { UieventhubService } from "@src/app/helpers/uieventhub/uieventhub.service";
import { UntilDestroy } from "@ngneat/until-destroy";
import { firstValueFrom, forkJoin, of, from } from "rxjs";
import { map, switchMap, catchError } from "rxjs/operators";
import { Person } from "./model/person";
import { Customer } from "@src/app/models/customer";
import { DynamicstyleService } from "@src/app/helpers/dynamicstyle/service/dynamicstyle.service";

@UntilDestroy({ checkProperties: true })
@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.css"],
  animations: [
    trigger("inbgOutAnimation", [
      transition(":enter", [
        style({ opacity: 0, zIndex: 4, position: "absolute" }),
        animate("1s ease-out", style({ opacity: 1 })),
      ]),
      transition(":leave", [
        style({ opacity: 0, zIndex: 4, position: "absolute" }),
        animate("1s ease-in", style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class LoginComponent implements OnInit {
  title = "ClearRamp";
  favIcon: HTMLLinkElement = document.querySelector("#appIcon");

  @Output() username: string;
  @Output() password: string;
  @Output() buttonText: string;
  @Output() showSpinner: boolean;

  customerId: string;
  year: string;
  showContent: boolean;
  orgId: string;
  errorMessage: string;
  moduleWhitelabel: boolean;
  includeStyles: boolean;
  orgList: PersonOrganization[];
  showPassword: boolean;
  sessionStart: boolean;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private loginsrv = inject(LoginService);
  private titleService = inject(Title);
  private meta = inject(Meta);
  private datastore = inject(DatastoreService);
  private uieventhub = inject(UieventhubService);
  private dynamicstyleService = inject(DynamicstyleService);

  constructor() { }

  async ngOnInit() {
    setTimeout(() => {
      this.showContent = true;
    }, 500);
    this.showSpinner = true;
    this.titleService.setTitle(this.title);
    this.setupMetaTags();
    this.moduleWhitelabel = false;
    this.includeStyles = false;
    this.year = new Date().getFullYear().toString();
    this.favIcon.href = "favicon.ico";
    this.errorMessage = null;
    //if a 'token' param is present in the URL, call the token login api
    const token = this.route.snapshot.queryParams["token"];
    if (token) {
      //console.log('Token found in URL:', token);
      try {
        const userdata = await firstValueFrom(
          this.loginsrv.tokenLogin(token)
        );
        if (userdata && userdata.user) {
          this.initSession(userdata);
          return;
        }

        if (userdata && userdata.username) {
          this.username = userdata.username;
          this.buttonText = 'Sign in';
          this.showSpinner = false;
          return;
        }

        this.errorMessage = 'Invalid invite token response.';
        this.showSpinner = false;
      } catch (error) {
        // Token invalid or already used — try resuming an existing session via
        // the refresh token cookie before falling back to the login form.
        try {
          await firstValueFrom(this.loginsrv.getMe());
          this.initSession();
          return;
        } catch (_sessionError) {
          // No valid session either; show login form without an error.
          this.buttonText = 'Sign in';
          this.showSpinner = false;
        }
      }
      return;
    }
    //if username is in URL like /login/myusername set it to the username field
    //console.log('route', this.route.snapshot);
    this.username = this.route.snapshot.url[1]
      ? this.route.snapshot.url[1].path
      : "";

    if (this.username && this.username.length > 0) {
      this.buttonText = "Sign in";
      this.showSpinner = false;
    } else {
      this.initSession();
    }
  }

  showPasswordToggle() {
    this.showPassword = !this.showPassword;
  }

  onEnter() {
    this.signIn();
  }

  private setupMetaTags() {
    this.meta.addTags([
      { charset: "UTF-8" },
      { property: "og:site_name", content: "ClearRamp" },
      { property: "og:title", content: "ClearRamp" },
      { property: "og:url", content: "https://www.app.clearramp.com" },
      { property: "og:type", content: "website" },
      {
        property: "og:description",
        content:
          "Do more with ClearRamp, a platform that enables optimizing your valuechain.",
      },
      { itemprop: "name", content: "ClearRamp" },
      { itemprop: "url", content: "https://www.app.clearramp.com" },
      {
        itemprop: "description",
        content:
          "Do more with ClearRamp, a platform that enables optimizing your valuechain",
      },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "date", content: "2022-04-27", scheme: "YYYY-MM-DD" },
      {
        name: "keywords",
        content: "sign in app ClearRamp simple tool structure",
      },
      { name: "robots", content: "noindex" },
      { name: "googlebot", content: "noindex" },
      {
        name: "description",
        content:
          "Do more with ClearRamp, a platform that enables optimizing your valuechain",
      },
    ]);
  }

  async signIn() {
    this.uieventhub.addLoader("projectportal");
    this.buttonText = "Signing in...";
    this.showSpinner = true;

    try {
      const userdata = await firstValueFrom(
        this.loginsrv.login({
          username: this.username,
          password: this.password,
        })
      );
      this.initSession(userdata);
    } catch (error) {
      console.error("SignIn error", error);
      this.errorMessage = error.message || "An error occurred";
      this.showSpinner = false;
    }
  }

  private async initSession(usr: any = null) {
    try {
      if(!usr){
        usr = {
          user: {
            sub: SessionHelper.getUserSub(),
            username: SessionHelper.getUserName(),
          },
        }
      }
      const { person, customer, customerUser, organizations } = await firstValueFrom(
        this.loginsrv.getMe()
      );
      let pers: Person = JSON.parse(person);
      let cust: Customer = customer ? JSON.parse(customer) : null;
      const custUser: any = customerUser ? JSON.parse(customerUser) : null;
      let orgs: PersonOrganization[] = JSON.parse(organizations);
      const resolvedUserSub = this.resolveUserSub(usr, pers, custUser);
      const resolvedUsername = this.resolveUsername(usr);
     //console.log('initSession', usr, pers, cust, orgs);
      this.datastore.clearData();
     //console.log('initSession', pers);
      SessionHelper.setUserSub(resolvedUserSub || pers.personId);
      if (resolvedUsername) {
        SessionHelper.setUserName(resolvedUsername);
      }
      SessionHelper.setUserEmail(pers?.email || custUser?.email || resolvedUsername);
      this.orgId = this.resolveOrgId(usr, pers, cust, orgs);

      if (!resolvedUserSub || !this.orgId) {
        throw new Error("Could not initialize session identity context");
      }

      const hasCustomerRecord = Boolean(cust && (cust as any).customerId);
      const hasCustomerUserRecord = Boolean(custUser && (custUser.customerId || custUser.organizationId));

      if (hasCustomerRecord || hasCustomerUserRecord) {
        const effectiveCustomer = hasCustomerRecord
          ? cust
          : ({
              customerId: custUser.customerId,
              organizationId: custUser.organizationId,
              customerType: custUser.customerType,
            } as Customer);

        await this.initCustomerUser(resolvedUserSub, effectiveCustomer, orgs);
      } else {
        await this.initStandardUser(pers, orgs);
      }
      
      // Notify UI components that organization and session data are fully set up
      this.uieventhub.setCurrentOrg(this.orgId);
    } catch (error) {
      console.error("Error initializing session", error);
      this.buttonText = "Sign in";
      this.showSpinner = false;
    }
  }

  private resolveUserSub(usr: any, person: Person = null, customerUser: any = null): string {
    const tokenUser = usr && usr.user ? usr.user : {};
    return tokenUser.sub
      || tokenUser.id
      || (person && person.personId)
      || (customerUser && (customerUser.userId || customerUser.sub || customerUser.id))
      || SessionHelper.getUserSub();
  }

  private resolveUsername(usr: any): string {
    const tokenUser = usr && usr.user ? usr.user : {};
    return usr?.username || tokenUser.username || SessionHelper.getUserName();
  }

  private resolveOrgId(usr: any, person: Person = null, customer: Customer = null, orgs: any[] = []): string {
    if (person && (person as any).defaultorg) {
      return (person as any).defaultorg;
    }

    if (Array.isArray(orgs) && orgs.length > 0) {
      const firstOrg = orgs[0] as any;
      if (firstOrg?.organizationId) {
        return firstOrg.organizationId;
      }

      if (firstOrg?.id_text) {
        return firstOrg.id_text;
      }

      if (firstOrg?.id) {
        return String(firstOrg.id);
      }
    }

    if (customer && (customer as any).organizationId) {
      return (customer as any).organizationId;
    }

    const customerClaim = usr?.user?.["custom:customer"];
    if (customerClaim) {
      return customerClaim.split("_")[2] || '';
    }

    return SessionHelper.getChoosenOrg();
  }

  private async initCustomerUser(userSub: string, customer:Customer = null, orgs = []) {
    try {
      await firstValueFrom(this.loginsrv.refreshCustomerAuth(
        this.orgId,
        customer.customerId,
        userSub
      ));
    } catch (error) {
      // Some deployments do not expose this legacy refresh endpoint.
      // Continue initialization and rely on existing auth records.
      if (!error || error.status !== 404) {
        console.warn('Customer auth refresh unavailable, continuing session init.', error);
      }
    }
    // If customerType is missing, fetch the full customer data
    if (!customer.customerType) {
      try {
        const fullCustomer = await firstValueFrom(
          this.loginsrv.getCustomer(this.orgId, customer.customerId)
        );
        // Merge the full customer data with existing customer object
        customer = { ...customer, ...fullCustomer } as Customer;
      } catch (error) {
        console.error('Error fetching full customer data:', error);
      }
    }
    
    // Set session data first
    SessionHelper.setChoosenOrg(this.orgId);
    SessionHelper.setCustomer(JSON.stringify(customer));
    SessionHelper.setPersonOrgs(JSON.stringify(orgs));
    
    // Parallelize independent API calls to improve performance
    try {
      const results = await firstValueFrom(forkJoin({
        auth: this.loginsrv.getPersonAuth(this.orgId, userSub),
        settings: this.getUserSettingsWithFallback(userSub),
        styles: this.loadDynamicStylesObservable(orgs),
        logos: this.loadOrgLogosObservable()
      }));
      
      // Apply results
      SessionHelper.setPersonAuth(JSON.stringify(results.auth));
      
      // Handle settings
      const settings = results.settings as any;
      if (settings && settings.personlanguage && settings.personlanguage !== "default") {
        SessionHelper.setLanguage(settings.personlanguage);
      } else if (settings && settings.orglanguage) {
        SessionHelper.setLanguage(settings.orglanguage);
      }
      
      // Handle styles
      if (results.styles) {
        SessionHelper.setDynamicStyle(JSON.stringify(results.styles));
      }
      
      // Logo results are already saved to sessionStorage by loadOrgLogosObservable
      
    } catch (error) {
      console.error('Error in parallel API calls:', error);
      // Fallback to sequential calls if parallel fails
      const auth = await firstValueFrom(this.loginsrv.getPersonAuth(this.orgId, userSub));
      SessionHelper.setPersonAuth(JSON.stringify(auth));
      await this.getSettings(userSub);
      await this.loadDynamicStyles();
      await this.loadOrgLogo();
    }
    
    this.includeStyles = true;
    this.datastore.load();

    const goto = this.route.snapshot.queryParams["returnUrl"];
    this.uieventhub.addLoader("projectportal");
    if(customer.customerType === 'light') {
      this.router.navigate(["/customerlight"]);
    } else {
      this.router.navigate([goto || "/customerportal"]);
    }
  }

  private async initStandardUser(
    person: Person = null,
    orgs: PersonOrganization[] = []
  ) {
    if (orgs.length == 0 && person.defaultorg) {
      //Get the org from the person
      let org = await firstValueFrom(this.loginsrv.getOrg(person.defaultorg));
      orgs.push({
        organizationId: org.organizationId,
        organization: org.organizationname,
        www: org.www,
        modules: org.modules,
        personId: person.personId,
        membername: person.fullname,
        date_joined: "-",
      });
    }
    
    // Set session data first
    SessionHelper.setPersonOrgs(JSON.stringify(orgs));
    SessionHelper.setChoosenOrg(this.orgId);
    SessionHelper.setPerson(JSON.stringify(person));

    // Parallelize independent API calls to improve performance
    try {
      const results = await firstValueFrom(forkJoin({
        auth: this.loginsrv.getPersonAuth(this.orgId, person.personId),
        settings: this.getUserSettingsWithFallback(person.personId),
        styles: this.loadDynamicStylesObservable(orgs),
        logos: this.loadOrgLogosObservable()
      }));
      
      // Apply results
      SessionHelper.setPersonAuth(JSON.stringify(results.auth));
      
      // Handle settings
      const settings = results.settings as any;
      if (settings && settings.personlanguage && settings.personlanguage !== "default") {
        SessionHelper.setLanguage(settings.personlanguage);
      } else if (settings && settings.orglanguage) {
        SessionHelper.setLanguage(settings.orglanguage);
      }
      
      // Handle styles
      if (results.styles) {
        SessionHelper.setDynamicStyle(JSON.stringify(results.styles));
      }
      
      // Logo results are already saved to sessionStorage by loadOrgLogosObservable
      
    } catch (error) {
      console.error('Error in parallel API calls:', error);
      // Fallback to sequential calls if parallel fails
      const auth = await firstValueFrom(
        this.loginsrv.getPersonAuth(this.orgId, person.personId)
      );
      SessionHelper.setPersonAuth(JSON.stringify(auth));
      await this.getSettings(person.personId);
      await this.loadDynamicStyles();
      await this.loadOrgLogo();
    }

    this.includeStyles = true;
    this.datastore.load();
    const goto = this.route.snapshot.queryParams["returnUrl"];
    this.uieventhub.addLoader("projectportal");
    this.router.navigate([goto || "/portal"]);
    // } else {
    //   const orgalist = await firstValueFrom(this.loginsrv.getPersonOrgList(usr.user.sub));
    //   //await firstValueFrom(this.loginsrv.refreshPersonAuth(orgalist[0].organizationId, usr.user.sub));
    //console.log('orgalist', orgalist);

    //   if (orgalist.length > 0) {
    //     const auth = await firstValueFrom(this.loginsrv.getPersonAuth(orgalist[0].organizationId, usr.user.sub));
    //     SessionHelper.setPersonAuth(JSON.stringify(auth));
    //     SessionHelper.setPersonOrgs(JSON.stringify(orgalist));
    //     SessionHelper.setChoosenOrg(pers.defaultorg || orgalist[0].organizationId);
    //     this.getSettings(usr.user.sub);
    //     SessionHelper.setPerson(JSON.stringify(pers));
    //     this.datastore.load();
    //   }

    //   const goto = this.route.snapshot.queryParams['returnUrl'];
    //   this.showSpinner = false;
    //   this.uieventhub.addLoader('projectportal');
    //   this.router.navigate([goto || '/portal']);
    // }
  }

  async getSettings(personId: string) {
    try {
      const setting = await firstValueFrom(
        this.loginsrv.getUserSettings(personId)
      );
      //console.log("User settings", setting);
      if (!setting || setting?.personlanguage =="default") {
        SessionHelper.setLanguage('en');
      } else {
        SessionHelper.setLanguage(setting.personlanguage);
      }
    } catch (error) {
      console.error("Settings error", error);
      SessionHelper.setLanguage('en');
    }
  }

  private async loadDynamicStyles() {
    // Check if whitelabel module is enabled
    const isWhitelabeled = this.isWhitelabelEnabled();
    
    try {
      if (isWhitelabeled) {
        // Load organization-specific styles
        const styles = await firstValueFrom(
          this.dynamicstyleService.getOrgstyles(this.orgId)
        );
        if (styles && styles.length > 0) {
          SessionHelper.setDynamicStyle(JSON.stringify(styles[0]));
          //console.log("Dynamic styles cached for organization:", this.orgId);
        }
      } else {
        // Load default styles
        const defaultStyle = await firstValueFrom(
          this.dynamicstyleService.getDefaultOrgStyle()
        );
        if (defaultStyle) {
          SessionHelper.setDynamicStyle(JSON.stringify(defaultStyle));
          //console.log("Default dynamic styles cached");
        }
      }
    } catch (error) {
      console.error("Error loading dynamic styles:", error);
      // Don't block login flow if styles fail to load
    }
  }

  private isWhitelabelEnabled(): boolean {
    // Check if the whitelabel module is enabled in the organization
    const personOrgsJson = SessionHelper.getPersonOrgs();
    if (!personOrgsJson) return false;

    try {
      const orgs = JSON.parse(personOrgsJson);
      const currentOrg = Array.isArray(orgs) 
        ? orgs.find(org => org.organizationId === SessionHelper.getChoosenOrg())
        : orgs;
      
      if (currentOrg && currentOrg.modules) {
        return currentOrg.modules.indexOf('whitelabel') >= 0;
      }
    } catch (error) {
      console.error("Error checking whitelabel status:", error);
    }
    
    return false;
  }

  private getUserSettingsWithFallback(personId: string) {
    return this.loginsrv.getUserSettings(personId).pipe(
      map((setting: any) => {
        if (!setting || setting?.personlanguage === "default") {
          return Promise.resolve({
            personlanguage: "default",
            orglanguage: "en"
          });
        }
        return setting;
      }),
      switchMap((setting: any) => {
        // Handle the case where setting is a Promise (from org settings fallback)
        return setting instanceof Promise ? from(setting) : of(setting);
      })
    );
  }

  private loadDynamicStylesObservable(orgs: PersonOrganization[] = []) {
    // Check if whitelabel module is enabled using passed orgs parameter
    const isWhitelabeled = this.isWhitelabelEnabledWithOrgs(orgs);
    
    if (isWhitelabeled) {
      return this.dynamicstyleService.getOrgstyles(this.orgId).pipe(
        map((styles: any[]) => styles && styles.length > 0 ? styles[0] : null)
      );
    } else {
      return this.dynamicstyleService.getDefaultOrgStyle();
    }
  }

  private isWhitelabelEnabledWithOrgs(orgs: PersonOrganization[]): boolean {
    if (!orgs || orgs.length === 0) {
      return false;
    }

    try {
      const currentOrg = Array.isArray(orgs) 
        ? orgs.find(org => org.organizationId === this.orgId)
        : orgs;
      
      if (currentOrg && currentOrg.modules) {
        return currentOrg.modules.indexOf('whitelabel') >= 0;
      }
    } catch (error) {
      console.error("Error checking whitelabel status:", error);
    }
    
    return false;
  }

  private loadOrgLogosObservable() {
    const orgId = SessionHelper.getChoosenOrg();
    if (!orgId) return of(null);

    // Check cache first to avoid unnecessary API calls
    const rectCacheKey = `currentOrgLogoUrl-${orgId}_rect`;
    const squareCacheKey = `currentOrgLogoUrl-${orgId}_square`;
    
    const cachedRect = sessionStorage.getItem(rectCacheKey);
    const cachedSquare = sessionStorage.getItem(squareCacheKey);
    
    // If both logos are cached, return immediately
    if (cachedRect && cachedSquare) {
      return of({ rect: cachedRect, square: cachedSquare });
    }

    // Fetch logos that are not cached
    const logoRequests: any = {};
    
    if (!cachedRect) {
      logoRequests.rect = this.loginsrv.getorgImageDB(orgId, "rect").pipe(
        map((rectImage: any) => {
          if (rectImage && rectImage !== "No image for this organization.") {
            if (!rectImage.type) {
              rectImage.type = "rect";
            }
            sessionStorage.setItem(rectCacheKey, rectImage.orgimageurl);
            return rectImage.orgimageurl;
          }
          return null;
        }),
        catchError((error) => {
          console.error("Error loading rectangular org logo", error);
          return of(null);
        })
      );
    } else {
      logoRequests.rect = of(cachedRect);
    }

    if (!cachedSquare) {
      logoRequests.square = this.loginsrv.getorgImageDB(orgId, "square").pipe(
        map((squareImage: any) => {
          if (squareImage && squareImage !== "No image for this organization.") {
            if (!squareImage.type) {
              squareImage.type = "square";
            }
            sessionStorage.setItem(squareCacheKey, squareImage.orgimageurl);
            return squareImage.orgimageurl;
          }
          return null;
        }),
        catchError((error) => {
          console.error("Error loading square org logo", error);
          return of(null);
        })
      );
    } else {
      logoRequests.square = of(cachedSquare);
    }

    return forkJoin(logoRequests);
  }

  private async loadOrgLogo() {
    const orgId = SessionHelper.getChoosenOrg();
    if (!orgId) return;

    // Check cache first to avoid unnecessary API calls
    const rectCacheKey = `currentOrgLogoUrl-${orgId}_rect`;
    const squareCacheKey = `currentOrgLogoUrl-${orgId}_square`;
    
    if (sessionStorage.getItem(rectCacheKey) && sessionStorage.getItem(squareCacheKey)) {
      return; // Both logos are already cached
    }

    try {
      if (!sessionStorage.getItem(rectCacheKey)) {
        const rectImage = await firstValueFrom(
          this.loginsrv.getorgImageDB(orgId, "rect")
        );
        if (rectImage && rectImage !== "No image for this organization.") {
          if (!rectImage.type) {
            rectImage.type = "rect";
          }
          sessionStorage.setItem(rectCacheKey, rectImage.orgimageurl);
        }
      }
    } catch (error) {
      console.error("Error loading rectangular org logo", error);
    }

    try {
      if (!sessionStorage.getItem(squareCacheKey)) {
        const squareImage = await firstValueFrom(
          this.loginsrv.getorgImageDB(orgId, "square")
        );
        if (squareImage && squareImage !== "No image for this organization.") {
          if (!squareImage.type) {
            squareImage.type = "square";
          }
          sessionStorage.setItem(squareCacheKey, squareImage.orgimageurl);
        }
      }
    } catch (error) {
      console.error("Error loading square org logo", error);
    }
  }
}
